import { useLayoutEffect, useSyncExternalStore } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from '@/lib/query-client';

interface ProvidersProps {
  children: React.ReactNode;
}

/*
 * Branded toaster hand-off (creator-shortlist-refinements-v2 Phase 8, Amendment 1).
 *
 * sonner 1.7.4 has no `toasterId`: every mounted `<Toaster>` subscribes to the one
 * global toast store, so a second Toaster would render every toast twice (and
 * announce it twice). The branded magic-link shell needs its toasts INSIDE its
 * themed scope, so while a shell is mounted the global Toaster below stands down
 * and the shell mounts its own.
 *
 * The signal lives HERE, on the eager side, on purpose: `BrandedThemeProvider`
 * statically imports `branded.css` (the Arial-stack theme), which U5 allows only in the lazy
 * branded route chunk — so this module must never import it. The shell imports
 * this hook instead. `Providers` also sits outside `RouterProvider`, so a
 * route-based check is not available here.
 */
let brandedToasterClaims = 0;
const claimListeners = new Set<() => void>();

function subscribeToClaims(listener: () => void): () => void {
  claimListeners.add(listener);
  return () => {
    claimListeners.delete(listener);
  };
}

function getClaimCount(): number {
  return brandedToasterClaims;
}

/**
 * Phase 8 fix packet G5 (security gate LOW-2) — the count is CLAMPED at zero.
 *
 * A count above zero unmounts the global portal `<Toaster>` process-wide, and some
 * portal surfaces report failures through `toast.error` ONLY (e.g.
 * `features/client/pages/ReviewDetailPage.tsx` on a failed decision). So a count
 * that ever drifted negative — a double release, an unmatched cleanup — would make
 * the next claim/release pair leave the counter stuck above zero and silently
 * swallow those messages, showing a client nothing at all after a failed approval.
 * Clamping keeps "no claims left" and "no Toaster" from ever disagreeing.
 */
function setClaimCount(next: number): void {
  brandedToasterClaims = Math.max(0, next);
  claimListeners.forEach((listener) => listener());
}

/**
 * Called by a component that renders its own `<Toaster>`; the global Toaster is
 * unmounted for as long as at least one caller is mounted. A layout effect, so the
 * hand-off commits before any child's passive effect (where a toast could fire).
 */
export function useClaimScopedToaster(): void {
  useLayoutEffect(() => {
    setClaimCount(brandedToasterClaims + 1);
    return () => setClaimCount(brandedToasterClaims - 1);
  }, []);
}

/**
 * The toast live-region wrapper, shared so the global and branded Toasters carry
 * byte-identical announcement semantics (only one of them is ever mounted).
 */
export function ToastLiveRegion({ children }: { children: React.ReactNode }) {
  return (
    <div aria-live="polite" aria-atomic="true">
      {children}
    </div>
  );
}

export function Providers({ children }: ProvidersProps) {
  const scopedToasterClaims = useSyncExternalStore(subscribeToClaims, getClaimCount, getClaimCount);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {scopedToasterClaims === 0 && (
        <ToastLiveRegion>
          <Toaster position="top-right" richColors closeButton />
        </ToastLiveRegion>
      )}
    </QueryClientProvider>
  );
}
